/**
 * VendorReadinessPanel — Vendor PQC Readiness tab on project-detail.
 * Styled with the QRAM design system (ink palette, btn-primary/secondary,
 * rounded-sm cards, uppercase tracking table headers).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/client';

interface VprAssessment {
  id: string;
  vendor_name: string;
  product_in_scope: string | null;
  state: string;
  catalog_version: string;
  cycle_number: number;
  updated_at: string;
}

const STATE_LABELS: Record<string, string> = {
  DRAFT: 'Draft', ISSUED: 'Issued to vendor', RECEIVED: 'Received',
  IMPORT_FAILED: 'Import failed', IMPORTED: 'Imported', UNDER_REVIEW: 'Under review',
  INVALID: 'Invalid', CONFIRMED: 'Confirmed', FED: 'Fed to Supply Chain', REOPENED: 'Reopened',
};

const STATE_TONE: Record<string, string> = {
  IMPORT_FAILED: 'text-red-600', INVALID: 'text-red-600',
  CONFIRMED: 'text-emerald-700', FED: 'text-emerald-700',
};

/** The public tokenised vendor form is off installation-wide unless VPR_PUBLIC_FORM_ENABLED=true. */
function PublicFormNote({ projectId }: { projectId: string }) {
  const q = useQuery({
    queryKey: ['vendor-readiness-setting', projectId],
    queryFn: () => api.get<{ enabled: boolean; public_form_enabled: boolean }>(`/projects/${projectId}/vendor-readiness-setting`),
  });
  if (!q.data) return null;
  return (
    <p className="text-xs text-ink-500 mt-1" data-testid="vpr-public-form-note">
      {q.data.public_form_enabled
        ? 'The online vendor form is enabled on this installation.'
        : 'The online vendor form is off on this installation: download the vendor copy, send it to the vendor and upload the returned spreadsheet.'}
    </p>
  );
}

export function VendorReadinessPanel({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const [vendorName, setVendorName] = useState('');
  const [product, setProduct] = useState('');
  const [error, setError] = useState<string | null>(null);

  const listQ = useQuery({
    queryKey: ['vendor-readiness', projectId],
    queryFn: () => api.get<VprAssessment[]>(`/projects/${projectId}/vendor-readiness`),
  });

  const createM = useMutation({
    mutationFn: () =>
      api.post<VprAssessment>(`/projects/${projectId}/vendor-readiness`, {
        vendor_name: vendorName.trim(),
        product_in_scope: product.trim() || null,
      }),
    onSuccess: () => {
      setVendorName(''); setProduct(''); setError(null);
      void qc.invalidateQueries({ queryKey: ['vendor-readiness', projectId] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Create failed'),
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-medium text-sm text-ink-900">Vendor PQC Readiness</h2>
        <p className="text-xs text-ink-500 mt-1">
          Evidence-backed vendor post-quantum readiness assessments. Confirmed results feed the
          Supply Chain module's Vendor Quantum Readiness parameter for the mapped assets.
        </p>
        <p className="text-xs text-ink-500 mt-1" data-testid="vpr-crosslinks">
          Optional module (Settings → Optional modules). Where the other vendor data lives: the vendor
          of each asset and its Supply chain inputs are on the asset form; each vendor&apos;s post-quantum
          commitment, used by the time tests, is in Settings → Reporting profile → Vendor commitments.
        </p>
        <PublicFormNote projectId={projectId} />
      </div>

      <div className="border border-ink-100 rounded-sm p-4 space-y-3 bg-white">
        <div className="text-[11px] font-medium text-ink-500 uppercase tracking-wider">New assessment</div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-ink-700">
            <span className="block mb-1">Vendor name</span>
            <input
              className="border border-ink-200 rounded-sm px-2 py-1.5 text-sm w-56 focus:outline-none focus:border-accent/50"
              value={vendorName} onChange={(e) => setVendorName(e.target.value)} maxLength={255}
            />
          </label>
          <label className="text-xs text-ink-700">
            <span className="block mb-1">Product / service in scope</span>
            <input
              className="border border-ink-200 rounded-sm px-2 py-1.5 text-sm w-72 focus:outline-none focus:border-accent/50"
              value={product} onChange={(e) => setProduct(e.target.value)} maxLength={500}
            />
          </label>
          <button
            className="btn-primary text-xs"
            disabled={!vendorName.trim() || createM.isPending}
            onClick={() => createM.mutate()}
          >
            Create
          </button>
        </div>
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>

      <div className="border border-ink-100 rounded-sm bg-white">
        {listQ.isLoading && <p className="py-8 text-center text-ink-500 text-sm">Loading…</p>}
        {listQ.isError && <p className="py-8 text-center text-red-600 text-sm">Failed to load assessments.</p>}
        {listQ.data && listQ.data.length === 0 && (
          <p className="py-8 text-center text-ink-500 text-sm">
            No assessments yet — create one above, download the vendor copy, and send it to the vendor.
          </p>
        )}
        {listQ.data && listQ.data.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-ink-50 border-b border-ink-100">
                <th className="text-left text-[11px] font-medium text-ink-500 uppercase tracking-wider px-3 py-2">Vendor</th>
                <th className="text-left text-[11px] font-medium text-ink-500 uppercase tracking-wider px-3 py-2">Product</th>
                <th className="text-left text-[11px] font-medium text-ink-500 uppercase tracking-wider px-3 py-2">State</th>
                <th className="text-right text-[11px] font-medium text-ink-500 uppercase tracking-wider px-3 py-2">Cycle</th>
                <th className="text-right text-[11px] font-medium text-ink-500 uppercase tracking-wider px-3 py-2">Updated</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {listQ.data.map((a) => (
                <tr key={a.id} className="border-b border-ink-100 last:border-0 hover:bg-ink-50/50 transition-colors">
                  <td className="px-3 py-2 font-medium text-ink-900">{a.vendor_name}</td>
                  <td className="px-3 py-2 text-ink-700">{a.product_in_scope ?? '—'}</td>
                  <td className={`px-3 py-2 text-xs ${STATE_TONE[a.state] ?? 'text-ink-700'}`}>
                    {STATE_LABELS[a.state] ?? a.state}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{a.cycle_number}</td>
                  <td className="px-3 py-2 text-right text-xs text-ink-500">
                    {new Date(a.updated_at).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      className="text-xs text-ink-400 hover:text-accent"
                      to={`/projects/${projectId}/vendor-readiness/${a.id}`}
                    >
                      Open →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
